import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface ViewDecisionNodeModalProps {
  open: boolean;
  onClose: () => void;
}

const formatJson = (value: any): string => {
  if (value === null || value === undefined) return '(vacío)';
  try {
    return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2);
  } catch {
    return String(value);
  }
};

const ViewDecisionNodeModal: React.FC<ViewDecisionNodeModalProps> = ({ open, onClose }) => {
  const { selectedNode, loading } = useAppSelector((state) => state.decisionNodes);

  const jsonFields = [
    { label: 'Opciones (options)', value: selectedNode?.options },
    { label: 'Configuración de Input (inputConfig)', value: selectedNode?.inputConfig },
    { label: 'Configuración Dinámica (dynamicOptionsConfig)', value: selectedNode?.dynamicOptionsConfig },
    { label: 'Configuración Terminal (terminalConfig)', value: selectedNode?.terminalConfig },
    { label: 'Actualizaciones de Contexto (contextUpdates)', value: selectedNode?.contextUpdates },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle de Nodo de Decisión</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedNode ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Clave del Árbol</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.treeKey}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Clave del Nodo</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.nodeKey}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Tipo de Nodo</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.nodeType}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Plantilla de Mensaje</Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{selectedNode.messageTemplate || '(vacío)'}</Typography>
              <Divider />
            </Box>

            {jsonFields.map((field) => (
              <Box key={field.label} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">{field.label}</Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 200,
                    fontSize: '0.85rem',
                    fontFamily: '"Fira Code", "Consolas", monospace',
                    mb: 2,
                  }}
                >
                  {formatJson(field.value)}
                </Box>
                <Divider />
              </Box>
            ))}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Nodo Padre (parentNodeKey)</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.parentNodeKey || '(vacío)'}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Siguiente Nodo (nextNode)</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.nextNode || '(vacío)'}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Siguiente Nodo al Seleccionar (nextNodeOnSelect)</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.nextNodeOnSelect || '(vacío)'}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Requiere Tenant</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.requiresTenant ? 'Sí' : 'No'}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Orden de Visualización</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.displayOrder}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Activo</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.isActive ? 'Sí' : 'No'}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Fecha de Creación</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.createdAt}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Creado Por</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.createdBy}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Fecha de Actualización</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedNode.updatedAt}</Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Actualizado Por</Typography>
              <Typography variant="body1">{selectedNode.updatedBy}</Typography>
            </Box>
          </Box>
        ) : (
          <Typography>No hay nodo seleccionado</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDecisionNodeModal;
